// @ts-check
// Comandos das campanhas de WhatsApp (spec-2.0.0 seção 5.3; spec/whatsapp.md 19; contrato 09 de WhatsApp):
// list.whatsappCampaign (público com os filtros da lista de cards) e selection.whatsappCampaign (público com os cards
// escolhidos, até 10.000). Os dois perguntam o tipo (Marketing ou Comunicado) num menu curto, criam o rascunho e abrem o
// editor. O registro é do passo 0 (client/src/lib/command-registry.js).
import { html } from 'htm/preact';
import { render } from 'preact';
import { navigate } from '../../app/router.js';
import { createWhatsappCampaign } from '../../data/whatsapp-campaigns.js';
import { getViewDraft } from '../views/view-state.js';
import { audienceFromRecordIds, audienceFromViewState } from '../../../../shared/audiences/audience-definition.js';
import { Modal } from '../../ui/modal.js';
import { enqueueSnackbar, showErrorSnackbar } from '../../ui/snackbar.js';
import { whatsappCampaignHref } from './whatsapp-campaigns-page.js';
import { t } from '../../i18n/index.js';

/** Comandos de WhatsApp prontos para aparecer. @type {ReadonlySet<string>} */
export const WHATSAPP_CAMPAIGN_COMMAND_IDS = new Set(['list.whatsappCampaign', 'selection.whatsappCampaign']);

/** Cards escolhidos que cabem no público de ids (acima disso, a lista filtrada). */
export const MAXIMUM_SELECTION_CARDS = 10_000;

/**
 * Pergunta o tipo da campanha num diálogo curto. Resolve com o tipo ou null (fechado).
 * @returns {Promise<'MARKETING' | 'ANNOUNCEMENT' | null>}
 */
function askCampaignKind() {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    let kind = /** @type {'MARKETING' | 'ANNOUNCEMENT'} */ ('MARKETING');
    /** @param {'MARKETING' | 'ANNOUNCEMENT' | null} value */
    const finish = (value) => {
      render(null, container);
      container.remove();
      resolve(value);
    };
    const draw = () => render(html`<${Modal} title=${t('whatsappCampaigns.commands.chooseKind')} subtitle=${t('whatsappCampaigns.commands.chooseKindHelp')}
      confirmLabel=${t('whatsappCampaigns.commands.create')} onClose=${() => finish(null)} onConfirm=${() => finish(kind)}>
      <fieldset class="wa-editor-kinds">
        <legend class="wa-visually-hidden">${t('whatsappCampaigns.editor.kindLabel')}</legend>
        ${['MARKETING', 'ANNOUNCEMENT'].map((option) => html`<label key=${option} class="wa-editor-kind" data-selected=${kind === option ? 'true' : undefined}>
          <input type="radio" name="wa-command-kind" value=${option} checked=${kind === option} onChange=${() => {
            kind = /** @type {'MARKETING' | 'ANNOUNCEMENT'} */ (option);
            draw();
          }} />
          <span><strong>${t(`whatsappCampaigns.kind.${option}`)}</strong><small>${t(option === 'MARKETING' ? 'whatsappCampaigns.newMarketingHint' : 'whatsappCampaigns.newAnnouncementHint')}</small></span>
        </label>`)}
      </fieldset>
    <//>`, container);
    draw();
  });
}

/**
 * Cria o rascunho com o público e abre o editor.
 * @param {'MARKETING' | 'ANNOUNCEMENT'} kind @param {any} audienceDefinition
 */
async function createDraftAndOpen(kind, audienceDefinition) {
  const campaign = await createWhatsappCampaign({ kind, audienceDefinition });
  navigate(whatsappCampaignHref(campaign.id));
}

/**
 * Executa um comando das campanhas de WhatsApp. Devolve false quando o comando não é desta área.
 * @param {import('../../lib/command-registry.js').CommandDescriptor} command
 * @param {import('../command-menu/command-actions.js').CommandRuntime} runtime
 * @returns {Promise<boolean>}
 */
export async function runWhatsappCampaignCommand(command, runtime) {
  if (!WHATSAPP_CAMPAIGN_COMMAND_IDS.has(command.id)) return false;
  try {
    if (command.id === 'selection.whatsappCampaign') {
      const selection = runtime.selection;
      if (!selection) return true;
      if (selection.count > MAXIMUM_SELECTION_CARDS) {
        enqueueSnackbar({ variant: 'error', message: t('whatsappCampaigns.commands.tooManyCards', { maximum: MAXIMUM_SELECTION_CARDS.toLocaleString('pt-BR') }) });
        return true;
      }
      const kind = await askCampaignKind();
      if (!kind) return true;
      const ids = await selection.resolveSelectedIds();
      await createDraftAndOpen(kind, audienceFromRecordIds(ids.slice(0, MAXIMUM_SELECTION_CARDS)));
      return true;
    }
    const list = runtime.list;
    if (!list) return true;
    const draft = getViewDraft(list.view);
    const copied = audienceFromViewState({ filters: draft.filters.value, filterGroups: draft.filterGroups.value, anyFieldSearch: draft.anyFieldSearch.value });
    if (copied.problems.length > 0) {
      enqueueSnackbar({ variant: 'error', message: t('whatsappCampaigns.commands.listTooComplex') });
      return true;
    }
    const kind = await askCampaignKind();
    if (!kind) return true;
    await createDraftAndOpen(kind, copied.definition);
  } catch (error) {
    showErrorSnackbar(error);
  }
  return true;
}
